/**
 * WHAT SHOULD IT HANDLE? — the planner's skip model (design_handoff_auth_profile_planner §5,
 * Sept 27 2026). A host can tell the planner not to do something: no permits for a backyard
 * get-together, no DJ, it's free. The flags travel to the API as `skip: { sound, staff, permits,
 * numbers, poster }` and every server stage honours them; here is the vocabulary, the presets,
 * the copy, and the reading of natural-language skips out of the prompt and notes.
 */

export const SKIPS = [
  { key: 'sound', label: 'SOUND', step: 4, why: 'No DJ or speakers. Bring your own playlist.' },
  { key: 'staff', label: 'STAFF', step: 5, why: 'No crew booked. You and your people run it.' },
  { key: 'permits', label: 'PERMITS', step: 6, why: 'Nothing checked or filed. Right for a get-together at home or a park.' },
  { key: 'numbers', label: 'TICKETS + PRICE', step: 7, why: 'Free to attend. No ticket, no break-even.' },
  { key: 'poster', label: 'POSTER', step: 8, why: 'No poster drawn.' },
];

export const KEY_BY_STEP = { 4: 'sound', 5: 'staff', 6: 'permits', 7: 'numbers', 8: 'poster' };
export const EMPTY_SKIP = { sound: false, staff: false, permits: false, numbers: false, poster: false };
/** JUST A GET-TOGETHER: sound, staff, permits and tickets + price off. The poster stays. */
export const GET_TOGETHER = { sound: true, staff: true, permits: true, numbers: true, poster: false };

export const normalizeSkip = (s) => ({ ...EMPTY_SKIP, ...Object.fromEntries(Object.entries(s || {}).filter(([k]) => k in EMPTY_SKIP).map(([k, v]) => [k, !!v])) });
export const anySkipped = (s) => Object.values(normalizeSkip(s)).some(Boolean);
export const sameSkip = (a, b) => SKIPS.every(({ key }) => !!a?.[key] === !!b?.[key]);
export const isGetTogether = (s) => !!s?.sound && !!s?.staff && !!s?.permits && !!s?.numbers;
export const skippedLabels = (s) => SKIPS.filter(({ key }) => s?.[key]).map(({ label }) => label.toLowerCase());

/**
 * Read skips out of free text — the prompt while it's typed, a note while the plan works. Only
 * ever turns a toggle ON: saying "no DJ" is an instruction, not saying it isn't a change of mind.
 */
export function readSkips(text, current) {
  const t = (text || '').toLowerCase();
  const n = normalizeSkip(current);
  if (/no (permits?|licen[cs]es?)|skip (the )?permits?|don.?t need (a )?permits?/.test(t)) n.permits = true;
  if (/no (dj|music|sound|speakers?)/.test(t)) n.sound = true;
  if (/no (staff|crew|bartenders?|servers?|security)/.test(t)) n.staff = true;
  if (/\bfree\b|no tickets?|no charge/.test(t)) n.numbers = true;
  if (/no poster/.test(t)) n.poster = true;
  if (/get.?together|just (friends|hanging)|casual/.test(t)) Object.assign(n, { sound: true, staff: true, permits: true, numbers: true });
  return n;
}

export function skipHint(s) {
  const off = skippedLabels(s);
  return off.length
    ? `Leaving out ${off.join(', ')}. Tap to add back.`
    : 'Tap anything it shouldn’t touch. You can also write it in the prompt: “no permits”, “no DJ”, “it’s free”.';
}

export const skipLogLine = (s) => { const off = skippedLabels(s); return off.length ? `Leaving out ${off.join(', ')}, as asked.` : null; };
